/** @jsxImportSource @emotion/react */

import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { useStudentKey } from '@hook/iyagigo/useInit';
import { actions as navigationActions, DEFAULT_IYAGIGO_SUB_MENU } from '@store/slice/navigation-slice';
import { useCurrentSubMenu } from '@hook/iyagigo/useSync';
import { useInfiniteScroll } from '@hook/common/useScroll';
import { TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { actions as modalActions } from '@store/slice/modal-slice';
import * as color from '@constant/color';
import * as size from '@constant/size';
import * as animation from '@constant/animation';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import OtterNudge from '@component/iyagigo/cloze/study/otter-nudge';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import ImageBlock from '@component/common/block/image-block';
import Gap from '@component/common/gap';
import otterTestImage from '@asset/image/otter-test.webp';
import WorkbookTemplate from '@component/iyagigo/common/workbook-template';
import Spinner from '@design-system/spinner';
import Canvas from '@design-system/canvas';
import Chip from '@design-system/chip';
import { createSpeakingPath } from '@constant/iyagigo/route-path';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import {
  useCreateSpeakingSessionMutation,
  useSpeakingSessionPageQuery,
  useSpeakingTasksQuery,
} from '@hook/iyagigo/useApi';
import type { ISpeakingTaskData } from '@datatype/iyagigo/api-type';

// 기본표현 홈과 같은 배너 — 수달 이미지 + 안내 문구
const BannerContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

const BannerContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 8px;
`;

// 기본표현 홈의 오늘의 학습 카드와 같은 형태
const TodayStudyContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const LessonCardsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const CardContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: ${size.MARGIN_1};
  width: 100%;
  padding: ${size.MARGIN_1};
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
  cursor: pointer;
`;

const CardContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`;

// 자유롭게 말하기 진입. 주제 목록과 함께 스크롤되면 안 되므로 뷰포트에 고정한다.
// 모바일은 화면 전체 폭, 웹은 사이드탭(WEB_SIDE_WIDTH) 오른쪽에 중앙 정렬된 ISLAND_WIDTH 기준으로 좌측 정렬한다.
const NUDGE_LEFT = IS_IYAGIGO_MOBILE_VIEW
  ? size.PAGE_PADDING
  : `calc(${size.WEB_SIDE_WIDTH} + (100% - ${size.WEB_SIDE_WIDTH} - ${size.ISLAND_WIDTH}) / 2 + ${size.PAGE_PADDING})`;

const NUDGE_BOTTOM = IS_IYAGIGO_MOBILE_VIEW
  ? `calc(${size.MOBILE_BOTTOM_HEIGHT} + var(--sab) + ${size.MARGIN_2})`
  : size.MARGIN_2;

const OtterNudgeContainer = styled.div<{ $isClosing: boolean }>`
  position: fixed;
  left: ${NUDGE_LEFT};
  bottom: ${NUDGE_BOTTOM};
  z-index: 5;

  ${(props) => (props.$isClosing ? animation.NUDGE_OUT_CSS : animation.NUDGE_IN_CSS)}
`;

const NudgeOpenButton = styled.div`
  position: fixed;
  left: ${NUDGE_LEFT};
  bottom: ${NUDGE_BOTTOM};
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: ${color.BROWN_1};
  box-shadow: ${color.SURFACE_SHADOW};
  cursor: pointer;
`;

// 과제 카드(CardContainer)와 같은 틀. 한 줄로 끝나지 않아 세로로 쌓는 점만 다르다.
const RecordCard = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 4px;
  width: 100%;
  padding: ${size.MARGIN_1};
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  border-radius: ${size.BORDER_RADIUS_1};
  background-color: ${color.WHITE_1};
  cursor: pointer;
`;

const RecordCardHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

// 기록이 없거나 불러오는 중. note-template의 EmptyContainer와 같은 모양으로 맞춘다.
const EmptyRecordContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: ${size.MARGIN_2} 0 0 0;
`;

// 기록 목록의 시작 시각. 연도는 빼고 '10월 5일 오후 7:32'처럼 분까지 보여 준다.
const formatRecordDateTime = (createdAt: string) => {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ko-KR', {
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

type TSpeakingTabId = NonNullable<TIyagigoSubMenu['speaking']>;

// 상단 탭. 말하기(과제 고르기) / 말하기 노트(지난 대화 목록)
const SPEAKING_TABS: { id: TSpeakingTabId; name: string }[] = [
  { id: 'talk', name: '말하기' },
  { id: 'record', name: '말하기 노트' },
];

const SPEAKING_SUB_MENU_IDS = ['talk', 'record'] as const;

const RECORD_DESCRIPTION_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left' },
} as const;

interface IProps {
  // 무한스크롤이 감시할 스크롤 컨테이너. 페이지가 Scroll을 쥐고 있어 거기서 내려받는다.
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

// 서버에 보내는 과제 key와 로딩을 표시할 클릭 위치는 별개다.
type TStudyTask = { type: 'today' | 'task'; taskKey: string } | { type: 'free-talk' };

const SpeakingHome: React.FC<IProps> = ({ scrollContainerRef }) => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  // 학습 언어는 학생 설정을 따른다. 영어가 아니면 일본어로 본다(다른 화면과 같은 방식).
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const apiLanguageCode =
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;
  const { data: tasksResponse } = useSpeakingTasksQuery(apiLanguageCode);
  const { studentKey } = useStudentKey();
  const { mutateAsync: createSpeakingSession, isPending: isCreatingSession } = useCreateSpeakingSessionMutation();
  const [studyTask, setStudyTask] = useState<TStudyTask | null>(null);
  const selectedTabId = useCurrentSubMenu(SPEAKING_SUB_MENU_IDS, DEFAULT_IYAGIGO_SUB_MENU.speaking as 'talk');
  const isRecordTab = selectedTabId === 'record';
  const sessionQuery = useSpeakingSessionPageQuery(studentKey ?? '', apiLanguageCode);
  const sessions = useMemo(
    () => sessionQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [sessionQuery.data],
  );
  const { sentinelRef } = useInfiniteScroll({
    hasNextPage: sessionQuery.hasNextPage ?? false,
    isFetchingNextPage: sessionQuery.isFetchingNextPage ?? false,
    fetchNextPage: sessionQuery.fetchNextPage,
    root: scrollContainerRef,
  });

  const handleTabClick = (id: TSpeakingTabId) => {
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'speaking', subMenu: id }));
  };
  const [nudgeState, setNudgeState] = useState<'open' | 'closing' | 'closed'>('open');
  const tasks = tasksResponse?.data ?? [];

  // 선택한 주제(stage)의 task만 보여 준다. 미선택이면 첫 stage(서버가 stageOrder로 정렬해 준다).
  const selectedStageKey =
    useTypedSelector((state) => state.rootReducer?.speakingReducer?.currentSpeakingStageKey) ?? null;
  const stages = tasks.reduce<{ stageKey: string; name: string }[]>((groups, task) => {
    const group = groups.find((item) => item.stageKey === task.stageKey);
    if (group) return groups;

    groups.push({ stageKey: task.stageKey, name: task.stageName?.ko ?? '' });
    return groups;
  }, []);
  const selectedStage = stages.find((stage) => stage.stageKey === selectedStageKey) ?? stages[0] ?? null;
  const selectedTasks = tasks.filter((task) => task.stageKey === selectedStage?.stageKey);

  // 오늘의 학습. 진도 기록이 없으므로 날짜로 하나를 골라 하루 동안 고정한다.
  // TODO: 학습 기록이 붙으면 '아직 안 한 것 중 첫 번째'로 바꾼다.
  const recommendedTask = selectedTasks.length ? selectedTasks[new Date().getDate() % selectedTasks.length] : null;

  const handleStartSession = async (target: TStudyTask) => {
    if (!studentKey || isCreatingSession || studyTask) return;

    setStudyTask(target);
    try {
      const { data } = await createSpeakingSession({
        studentKey,
        languageCode: apiLanguageCode,
        taskKey: target.type === 'free-talk' ? undefined : target.taskKey,
      });
      navigate(createSpeakingPath('session', data.key));
    } catch {
      // API 오류와 할당량 wall은 useCreateSpeakingSessionMutation에서 처리한다.
    } finally {
      setStudyTask(null);
    }
  };

  // 기록은 녹음 없이 지난 대화를 조회한다.
  const handleRecordClick = (sessionKey: string) => {
    navigate(`${createSpeakingPath('session', sessionKey)}?mode=record`);
  };

  const handleStageSelectClick = () => {
    dispatch(modalActions.showDialogModal({ type: 'speaking-stage-select' }));
  };

  // 자유 대화는 task 없이 세션을 만든다. 학습 화면은 조회한 세션 정보로 구분한다.
  const handleFreeTalkClick = () => {
    void handleStartSession({ type: 'free-talk' });
  };

  const handleNudgeCloseClick = () => {
    setNudgeState('closing');
    setTimeout(() => setNudgeState('closed'), 300);
  };

  const SpeakingTaskCards = (groupTasks: ISpeakingTaskData[]) =>
    groupTasks.map((task) => (
      <CardContainer key={task.key} onClick={() => void handleStartSession({ type: 'task', taskKey: task.key })}>
        <CardContent>
          <TextBlock
            text={task.taskName?.ko ?? ''}
            design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          <TextBlock
            text={task.description?.ko ?? ''}
            design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
          />
        </CardContent>
        {studyTask?.type === 'task' && studyTask.taskKey === task.key ? (
          <Spinner size='16px' color={color.BROWN_1} />
        ) : (
          <IconBlock
            icon='right'
            design={{ icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' }, box: {} }}
          />
        )}
      </CardContainer>
    ));

  // 기록 탭 — 지난 대화 목록. 누르면 그 세션을 다시 연다.
  const SessionCards = sessions.map((session) => (
    <RecordCard key={session.key} onClick={() => handleRecordClick(session.key)}>
      <RecordCardHeader>
        <TextBlock
          text={session.speakingTask?.taskName?.ko ?? '자유롭게 말하기'}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
        />
        <Chip
          text={session.completedAt ? '학습 완료' : '진행 중'}
          size='tiny'
          usage='static'
          kind='outlined'
          theme={session.completedAt ? ETheme.GREEN : ETheme.BLUE}
        />
      </RecordCardHeader>
      <TextBlock
        text={formatRecordDateTime(session.createdAt)}
        design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
      />
    </RecordCard>
  ));

  // 기록 탭 — 지난 대화 목록
  const SpeakingRecordContent = (
    <>
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt='수달 선생님' design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text='말하기 노트' design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }} />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_1} />
      <Canvas theme={ETheme.BROWN} shape='square' width='100%'>
        <TextBlock
          text={`<strong style="color: #A26939;">나눈 대화</strong>를 다시 듣고 따라 말할수록\n<strong style="color: #A26939;">말하기</strong>가 조금씩 늘어요`}
          design={RECORD_DESCRIPTION_DESIGN}
        />
      </Canvas>
      <Gap height={size.MARGIN_2} />
      {sessionQuery.isPending ? (
        <EmptyRecordContainer>
          <Spinner size='30px' color={color.BROWN_1} />
        </EmptyRecordContainer>
      ) : sessions.length ? (
        <>
          <LessonCardsContainer>{SessionCards}</LessonCardsContainer>
          <div ref={sentinelRef} style={{ width: '100%', height: '1px' }} />
        </>
      ) : (
        <EmptyRecordContainer>
          <TextBlock
            text='아직 나눈 대화가 없어요'
            design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { width: '100%', alignX: 'center' } }}
          />
        </EmptyRecordContainer>
      )}
    </>
  );

  const SpeakingTalkContent = (
    <>
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt='수달 선생님' design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text='현재 주제' design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          <TextBlock
            text={selectedStage?.name ?? ''}
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
          />
          <IconTextBlock
            icon='edit'
            text='주제 선택하기'
            arrangement='text-icon'
            design={{
              text: { size: 'tiny', color: color.GRAY_1, underline: false },
              icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' }, gap: '2px' },
            }}
            click={{ handler: handleStageSelectClick }}
          />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_2} />
      {!!recommendedTask && (
        <>
          <TextBlock
            text='오늘의 학습'
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          <Gap height={size.MARGIN_1} />
          <TodayStudyContainer>
            <TextBlock
              text={`<strong>${recommendedTask.taskName?.ko ?? ''}</strong>부터 시작해요`}
              design={{ text: { size: 'large', color: color.BLACK_2 }, box: { alignX: 'left' } }}
            />
            <Gap height='5px' />
            <TextBlock
              text={recommendedTask.description?.ko ?? ''}
              design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
            />
            <Gap height={size.MARGIN_1} />
            <TextButton
              text='시작하기'
              theme={ETheme.BROWN}
              size='medium'
              shape='pill'
              width='100%'
              isLoading={studyTask?.type === 'today'}
              handleClick={() => void handleStartSession({ type: 'today', taskKey: recommendedTask.key })}
            />
          </TodayStudyContainer>
          <Gap height={size.MARGIN_2} />
        </>
      )}
      <LessonCardsContainer>{SpeakingTaskCards(selectedTasks)}</LessonCardsContainer>
      {nudgeState === 'closed' ? (
        <NudgeOpenButton onClick={() => setNudgeState('open')}>
          <IconBlock icon='feed' design={{ icon: { size: 'medium', color: color.WHITE_1, kind: 'outlined' } }} />
        </NudgeOpenButton>
      ) : (
        <OtterNudgeContainer $isClosing={nudgeState === 'closing'}>
          <OtterNudge
            copyText='주제 없이 편하게 이야기해요'
            goText='자유롭게 말하기'
            handleGoClick={handleFreeTalkClick}
            handleCloseClick={handleNudgeCloseClick}
          />
        </OtterNudgeContainer>
      )}
    </>
  );

  return (
    <WorkbookTemplate
      tabs={SPEAKING_TABS}
      selectedTabId={selectedTabId}
      onTabClick={(id) => handleTabClick(id as TSpeakingTabId)}>
      {!isRecordTab && SpeakingTalkContent}
      {isRecordTab && SpeakingRecordContent}
    </WorkbookTemplate>
  );
};

export default SpeakingHome;
